import React from "react";
import { LocaleProvider } from "@insightfulscience/smart-react/i18";
import WithConditions from "@insightfulscience/smart-react/hocs/WithConditions";
import { createAndRaise } from "@insightfulscience/shared-utils/fn/create-and-raise";
import ApplicationError from "@insightfulscience/smart-react/ApplicationError";
import pipe from "@insightfulscience/shared-utils/fn/pipe";

import createClient from "../../createClient";
import UpdatesPage from "./Page";
import { PUBLISHED, RELEASE_NOTES_PER_PAGE } from "../../constants";
import { getQueryPage, paginationConfig, paginationInit } from "../../utils/pagination";
import locales from "../../locales";
import { sanitize } from "../../utils/contentfulSanitizer";

const ContentfulUpdatesFactory = ({ Layout, contentfulProps, pageProps }) => {
	const ContentfulUpdatesPage = ({ page }) => {
		const { brandCode, resources = null } = pageProps;

		const PageComponent = pageProps.PageComponent || UpdatesPage;

		return (
			<Layout>
				<LocaleProvider resources={resources || locales[brandCode]}>
					<PageComponent page={page} pageProps={pageProps} />
				</LocaleProvider>
			</Layout>
		);
	};

	ContentfulUpdatesPage.getInitialProps = async ({ query }) => {
		const page = getQueryPage(query);
		const client = createClient(contentfulProps);
		const latestVersionData = await client.getEntries({
			content_type: "pageReleaseNotes",
			order: "-fields.releaseDate",
			"fields.publishStatus": PUBLISHED,
			skip: 0,
			limit: 1,
		});

		const latestVersion = latestVersionData?.items[0]?.fields
			? {
					version: latestVersionData?.items[0]?.fields?.appVersionNumber || "",
					majorVersion: latestVersionData?.items[0]?.fields?.appVersionNumber.split(".")[0] || "",
					windowsInstallerUrl: latestVersionData?.items[0]?.fields?.windowsInstallerUrl || "",
					macInstallerUrl: latestVersionData?.items[0]?.fields?.macInstallerUrl || "",
					redHatInstallerUrl: latestVersionData?.items[0]?.fields?.redHatInstallerUrl || "",
					ubuntuInstallerUrl: latestVersionData?.items[0]?.fields?.ubuntuInstallerUrl || "",
			  }
			: {};
		const contentfulResponse = await client.getEntries({
			content_type: "pageReleaseNotes",
			order: "-fields.releaseDate",
			"fields.publishStatus": PUBLISHED,
			...paginationInit(RELEASE_NOTES_PER_PAGE, page),
		});

		if (contentfulResponse.total === 0) {
			return {
				page: null,
			};
		}

		return {
			page: {
				latestVersion,
				releaseNotes: sanitize(contentfulResponse.items),
				...paginationConfig(contentfulResponse.total, RELEASE_NOTES_PER_PAGE, page),
			},
		};
	};

	ContentfulUpdatesPage.propTypes = {
		...UpdatesPage.propTypes,
	};

	ContentfulUpdatesPage.postConditions = [
		[({ page }) => page, createAndRaise(ApplicationError.NotFound)],
	];

	return pipe(ContentfulUpdatesPage, WithConditions);
};

export default ContentfulUpdatesFactory;
